import axios from "axios";
import React, { useEffect, useState } from "react";
import { Col, Container, Row, Table, Modal, Button } from "react-bootstrap";
import Card from "../../../components/Card";
import { webServiceUrl } from "../../../components/apiCall/webserviceurl";
import { toast, ToastContainer } from "react-toastify";
import { Oval } from "react-loader-spinner";
import {
  FaAward,
  FaDownload,
  FaEye,
  FaCheckCircle,
  FaCertificate,
} from "react-icons/fa";

// -----------------------------------------------------------------------
// NOTE ON BACKEND CONTRACT (adjust to match your actual API):
//
// webServiceUrl.getCertificates -> POST { school_code, adm_no, student_name }
//   returns: { data: [{ certificate_id, course_name, completed_on, status,
//                        instructor_name }] }
//
// webServiceUrl.downloadCertificate -> POST { school_code, adm_no,
//   certificate_id }  responseType: "blob"  returns: application/pdf bytes
//
// If your endpoints already exist under different names, just swap the
// two axios calls below — everything else (UI, state, loading, download
// trigger) will keep working as-is.
// -----------------------------------------------------------------------

// -----------------------------------------------------------------------
// DUMMY DATA — used until the real getCertificates API is wired back up.
// Shape matches what the backend is expected to return. Remove/replace
// once webServiceUrl.getCertificates is live again.
// -----------------------------------------------------------------------
const DUMMY_CERTIFICATES = [
  {
    certificate_id: "IHFC-CERT-2026-0142",
    course_name: "Sustainability & ESG Fundamentals",
    completed_on: "2026-06-18",
    status: "Completed",
    instructor_name: "Dr. Ananya Rao",
  },
  {
    certificate_id: "IHFC-CERT-2026-0139",
    course_name: "ESMS Legal Compliance Training",
    completed_on: "2026-05-27",
    status: "Completed",
    instructor_name: "Rohit Malhotra",
  },
  {
    certificate_id: "IHFC-CERT-2026-0121",
    course_name: "Understanding a Sustainability Policy",
    completed_on: "2026-04-09",
    status: "Completed",
    instructor_name: "Priya Nair",
  },
  {
    certificate_id: "IHFC-CERT-2025-0987",
    course_name: "Workplace Health, Safety & Environment (EHS)",
    completed_on: "2025-12-14",
    status: "Completed",
    instructor_name: "Sameer Kulkarni",
  },
  {
    certificate_id: "IHFC-CERT-2025-0873",
    course_name: "Grievance Redressal Mechanism Training",
    completed_on: "2025-10-02",
    status: "Completed",
    instructor_name: "Neha Verma",
  },
];

function DownloadCertificate() {
  const course = JSON.parse(localStorage.getItem("course"));
  const courseData = course?.student;

  const [certificates, setCertificates] = useState(DUMMY_CERTIFICATES);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [activeCertificate, setActiveCertificate] = useState(null);

  const getCertificates = async () => {
    try {
      setLoading(true);
      const response = await axios.post(webServiceUrl.getCertificates, {
        school_code: course?.school_code,
        adm_no: courseData?.adm_no,
        student_name: courseData?.student_name,
      });
      setCertificates(response?.data?.data ?? []);
    } catch (error) {
      console.error(error);
      toast.error("Unable to load certificates right now.");
    } finally {
      setTimeout(() => setLoading(false), 800);
    }
  };

  //   useEffect(() => {
  //     getCertificates();
  //   }, []);

  const openPreview = (cert) => {
    setActiveCertificate(cert);
    setShowPreview(true);
  };

  const closePreview = () => {
    setShowPreview(false);
    setActiveCertificate(null);
  };

  const downloadCertificate = async (cert) => {
    try {
      setDownloadingId(cert.certificate_id);
      const response = await axios.post(
        webServiceUrl.downloadCertificate,
        {
          school_code: course?.school_code,
          adm_no: courseData?.adm_no,
          certificate_id: cert.certificate_id,
        },
        { responseType: "blob" },
      );

      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute(
        "download",
        `Certificate_${cert.course_name?.replace(/\s+/g, "_")}.pdf`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      toast.success("Certificate downloaded successfully!");
    } catch (error) {
      console.error("Download Certificate Error:", error);
      toast.error("Could not download certificate. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (isNaN(d)) return value;
    const day = d.getDate().toString().padStart(2, "0");
    const month = d.toLocaleString("default", { month: "long" });
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  };

  return (
    <div id="content-page" className="content-inner">
      <ToastContainer />
      <Container fluid>
        <Row className="justify-content-center">
          <Col lg="12">
            <Card
              className="shadow border-0 rounded-4 overflow-hidden px-2"
              style={{ background: "#ffffff" }}
            >
              <Card.Header
                className="mt-1 mt-md-4 py-3 border-0"
                style={{ background: "transparent" }}
              >
                <div className="d-flex flex-wrap align-items-center gap-2 me-2 me-md-4 my-3 my-lg-2">
                  <span
                    className="d-inline-flex align-items-center justify-content-center rounded-circle"
                    style={{
                      width: 42,
                      height: 42,
                      background: "#16a34a",
                      color: "#fff",
                      flexShrink: 0,
                    }}
                  >
                    <FaAward size={18} />
                  </span>
                  <div>
                    <h4
                      className="mb-0 fw-bold"
                      style={{ fontSize: "clamp(1.1rem, 4vw, 1.5rem)" }}
                    >
                      My Certificates
                    </h4>
                    <small className="text-muted d-block">
                      Preview and download certificates for completed trainings
                    </small>
                  </div>
                </div>
              </Card.Header>

              <Card.Body className="pt-0 px-2 px-md-3">
                {/* ---------- Desktop / tablet: table layout (md and up) ---------- */}
                <Row className="d-none d-md-flex">
                  <div className="table-responsive mt-2 rounded-4 overflow-hidden border">
                    <Table hover responsive className="mb-0 align-middle">
                      <thead>
                        <tr
                          style={{
                            background: "#f0fdf4",
                            borderBottom: "2px solid #16a34a",
                          }}
                        >
                          <th className="py-3 text-secondary text-uppercase small fw-bold">
                            Sr.No
                          </th>
                          <th className="py-3 text-secondary text-uppercase small fw-bold">
                            Training / Course
                          </th>
                          <th className="py-3 text-secondary text-uppercase small fw-bold">
                            Completed On
                          </th>
                          <th className="py-3 text-secondary text-uppercase small fw-bold">
                            Status
                          </th>
                          <th className="py-3 text-secondary text-uppercase small fw-bold text-center">
                            Actions
                          </th>
                        </tr>
                      </thead>

                      {loading ? (
                        <tbody>
                          <tr>
                            <td colSpan="5" className="border-0">
                              <div className="d-flex flex-column align-items-center justify-content-center py-5">
                                <Oval
                                  height="45"
                                  width="45"
                                  color="#16a34a"
                                  secondaryColor="#dcfce7"
                                />
                                <span className="text-muted small mt-2">
                                  Loading certificates...
                                </span>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      ) : (
                        <tbody>
                          {certificates.length > 0 ? (
                            certificates.map((cert, index) => (
                              <tr key={cert.certificate_id ?? index}>
                                <td className="text-muted">{index + 1}</td>
                                <td className="fw-semibold">
                                  {cert.course_name}
                                </td>
                                <td className="text-muted">
                                  {formatDate(cert.completed_on)}
                                </td>
                                <td>
                                  <span className="badge rounded-pill text-bg-success px-3 py-2 d-inline-flex align-items-center gap-1">
                                    <FaCheckCircle size={11} />
                                    {cert.status || "Completed"}
                                  </span>
                                </td>
                                <td className="text-center">
                                  <div className="d-flex flex-wrap align-items-center justify-content-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => openPreview(cert)}
                                      className="d-inline-flex align-items-center justify-content-center rounded-circle border-0"
                                      style={{
                                        width: 34,
                                        height: 34,
                                        background: "#f0fdf4",
                                        border: "1px solid #bbf7d0",
                                        color: "#16a34a",
                                        flexShrink: 0,
                                      }}
                                      title="Preview certificate"
                                    >
                                      <FaEye size={14} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => downloadCertificate(cert)}
                                      disabled={
                                        downloadingId === cert.certificate_id
                                      }
                                      className="btn d-flex align-items-center gap-2 px-3 py-2 rounded-pill fw-semibold border-0 text-nowrap"
                                      style={{
                                        background: "#16a34a",
                                        color: "#fff",
                                        boxShadow:
                                          "0 4px 14px rgba(22, 163, 74, 0.3)",
                                      }}
                                    >
                                      {downloadingId === cert.certificate_id ? (
                                        <Oval
                                          height="14"
                                          width="14"
                                          color="#ffffff"
                                          secondaryColor="#dcfce7"
                                        />
                                      ) : (
                                        <FaDownload size={12} />
                                      )}
                                      Download
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" className="border-0">
                                <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                                  <FaCertificate
                                    size={34}
                                    className="mb-2 opacity-50"
                                  />
                                  <span>No certificates available yet</span>
                                  <small>
                                    Certificates appear here once a training is
                                    completed
                                  </small>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      )}
                    </Table>
                  </div>
                </Row>

                {/* ---------- Mobile: stacked card layout (below md) ---------- */}
                <div className="d-md-none mt-2">
                  {loading ? (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5">
                      <Oval
                        height="42"
                        width="42"
                        color="#16a34a"
                        secondaryColor="#dcfce7"
                      />
                      <span className="text-muted small mt-2">
                        Loading certificates...
                      </span>
                    </div>
                  ) : certificates.length > 0 ? (
                    <div className="d-flex flex-column gap-3">
                      {certificates.map((cert, index) => (
                        <div
                          key={cert.certificate_id ?? index}
                          className="rounded-4 border p-3"
                          style={{ background: "#fafffb" }}
                        >
                          <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                            <h6
                              className="fw-bold mb-0"
                              style={{ maxWidth: "75%" }}
                            >
                              {cert.course_name}
                            </h6>
                            <span className="badge rounded-pill text-bg-success px-2 py-2 d-inline-flex align-items-center gap-1 flex-shrink-0">
                              <FaCheckCircle size={10} />
                              {cert.status || "Completed"}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between text-muted small mb-3">
                            <span>Completed on</span>
                            <span className="fw-semibold text-dark">
                              {formatDate(cert.completed_on)}
                            </span>
                          </div>
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              onClick={() => openPreview(cert)}
                              className="btn flex-fill d-flex align-items-center justify-content-center gap-2 rounded-pill fw-semibold"
                              style={{
                                background: "#f0fdf4",
                                border: "1px solid #bbf7d0",
                                color: "#16a34a",
                              }}
                            >
                              <FaEye size={13} />
                              Preview
                            </button>
                            <button
                              type="button"
                              onClick={() => downloadCertificate(cert)}
                              disabled={downloadingId === cert.certificate_id}
                              className="btn flex-fill d-flex align-items-center justify-content-center gap-2 rounded-pill fw-semibold border-0"
                              style={{
                                background: "#16a34a",
                                color: "#fff",
                                boxShadow: "0 4px 14px rgba(22, 163, 74, 0.3)",
                              }}
                            >
                              {downloadingId === cert.certificate_id ? (
                                <Oval
                                  height="14"
                                  width="14"
                                  color="#ffffff"
                                  secondaryColor="#dcfce7"
                                />
                              ) : (
                                <FaDownload size={13} />
                              )}
                              Download
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted rounded-4 border">
                      <FaCertificate size={30} className="mb-2 opacity-50" />
                      <span>No certificates available yet</span>
                      <small className="text-center px-3">
                        Certificates appear here once a training is completed
                      </small>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* ---------------------- Certificate Preview Modal ---------------------- */}
      <Modal
        show={showPreview}
        onHide={closePreview}
        centered
        size="lg"
        fullscreen="sm-down"
        contentClassName="rounded-4 rounded-sm-4 border-0 overflow-hidden"
      >
        <Modal.Body className="p-0">
          <div
            className="position-relative"
            style={{
              background: "#ffffff",
              padding: "clamp(6px, 2vw, 10px)",
            }}
          >
            {/* Certificate card */}
            <div
              style={{
                border: "3px solid #16a34a",
                borderRadius: "18px",
                padding: "clamp(24px, 6vw, 40px) clamp(16px, 5vw, 36px)",
                position: "relative",
                background:
                  "repeating-linear-gradient(135deg, #ffffff, #ffffff 40px, #f7fdf9 40px, #f7fdf9 80px)",
                textAlign: "center",
              }}
            >
              {/* corner accents */}
              <div
                style={{
                  position: "absolute",
                  top: "clamp(6px, 2vw, 10px)",
                  left: "clamp(6px, 2vw, 10px)",
                  width: "clamp(16px, 4vw, 26px)",
                  height: "clamp(16px, 4vw, 26px)",
                  borderTop: "3px solid #16a34a",
                  borderLeft: "3px solid #16a34a",
                  borderRadius: "6px 0 0 0",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: "clamp(6px, 2vw, 10px)",
                  right: "clamp(6px, 2vw, 10px)",
                  width: "clamp(16px, 4vw, 26px)",
                  height: "clamp(16px, 4vw, 26px)",
                  borderTop: "3px solid #16a34a",
                  borderRight: "3px solid #16a34a",
                  borderRadius: "0 6px 0 0",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: "clamp(6px, 2vw, 10px)",
                  left: "clamp(6px, 2vw, 10px)",
                  width: "clamp(16px, 4vw, 26px)",
                  height: "clamp(16px, 4vw, 26px)",
                  borderBottom: "3px solid #16a34a",
                  borderLeft: "3px solid #16a34a",
                  borderRadius: "0 0 0 6px",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: "clamp(6px, 2vw, 10px)",
                  right: "clamp(6px, 2vw, 10px)",
                  width: "clamp(16px, 4vw, 26px)",
                  height: "clamp(16px, 4vw, 26px)",
                  borderBottom: "3px solid #16a34a",
                  borderRight: "3px solid #16a34a",
                  borderRadius: "0 0 6px 0",
                }}
              />

              <span
                className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                style={{
                  width: "clamp(48px, 12vw, 64px)",
                  height: "clamp(48px, 12vw, 64px)",
                  background: "#16a34a",
                  color: "#fff",
                  boxShadow: "0 6px 18px rgba(22, 163, 74, 0.35)",
                }}
              >
                <FaAward size={24} className="d-none d-sm-inline" />
                <FaAward size={20} className="d-inline d-sm-none" />
              </span>

              <h6
                className="text-uppercase fw-bold mb-1"
                style={{
                  letterSpacing: "clamp(1px, 0.6vw, 3px)",
                  color: "#16a34a",
                  fontSize: "clamp(0.75rem, 2.2vw, 1rem)",
                }}
              >
                IHFC Training Institute
              </h6>
              <p
                className="text-muted mb-4"
                style={{ fontSize: "clamp(0.75rem, 2vw, 0.9rem)" }}
              >
                Certificate of Completion
              </p>

              <p
                className="text-muted mb-1"
                style={{ fontSize: "clamp(0.85rem, 2.2vw, 1rem)" }}
              >
                This certifies that
              </p>
              <h2
                className="fw-bold mb-3 text-break"
                style={{
                  color: "#14532d",
                  fontFamily: "Georgia, serif",
                  fontSize: "clamp(1.4rem, 5.5vw, 2.25rem)",
                }}
              >
                {courseData?.student_name || "Student Name"}
              </h2>
              <p
                className="text-muted mb-1"
                style={{ fontSize: "clamp(0.85rem, 2.2vw, 1rem)" }}
              >
                has successfully completed
              </p>
              <h4
                className="fw-semibold mb-4 text-break"
                style={{
                  color: "#16a34a",
                  fontSize: "clamp(1rem, 3.8vw, 1.5rem)",
                }}
              >
                {activeCertificate?.course_name || "Training Program"}
              </h4>

              <Row className="justify-content-center mt-4 g-3">
                <Col xs={12} sm={5} className="border-top pt-2">
                  <small className="text-muted d-block">Date</small>
                  <span className="fw-semibold">
                    {formatDate(activeCertificate?.completed_on)}
                  </span>
                </Col>
                <Col xs={12} sm={5} className="border-top pt-2">
                  <small className="text-muted d-block">Certificate ID</small>
                  <span
                    className="fw-semibold d-inline-block text-break"
                    style={{ fontSize: "clamp(0.8rem, 2.2vw, 1rem)" }}
                  >
                    {activeCertificate?.certificate_id || "—"}
                  </span>
                </Col>
              </Row>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0 px-3 px-md-4 pb-4 pt-0 flex-column flex-sm-row justify-content-center gap-2">
          <Button
            variant="light"
            onClick={closePreview}
            className="px-4 rounded-pill fw-semibold w-100 w-sm-auto order-2 order-sm-1"
          >
            Close
          </Button>
          <Button
            onClick={() => {
              downloadCertificate(activeCertificate);
            }}
            disabled={downloadingId === activeCertificate?.certificate_id}
            className="px-4 rounded-pill fw-semibold border-0 d-flex align-items-center justify-content-center gap-2 w-100 w-sm-auto order-1 order-sm-2"
            style={{ background: "#16a34a" }}
          >
            <FaDownload size={12} />
            Download PDF
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default DownloadCertificate;
